/**
 * Singleton keycloak-js (modèle Renewr) — spec §5.1.
 *
 * Toute la SPA passe par ces fonctions : personne n'instancie Keycloak ailleurs,
 * personne n'appelle `updateToken()` en direct.
 */
import Keycloak from 'keycloak-js';
import type { RuntimeConfig } from '@/lib/config';

/** Marge par défaut (secondes) avant expiration à partir de laquelle on rafraîchit. */
const DEFAULT_MIN_VALIDITY = 70;

/** Période de la boucle de rafraîchissement de fond (ms). */
const REFRESH_LOOP_MS = 15_000;

/** Délai au-delà duquel `init()` est considéré comme perdu (ms). */
const INIT_TIMEOUT_MS = 10_000;

let keycloak: Keycloak | null = null;

/**
 * Attentes de rafraîchissement en vol, indexées par `minValidity` : N appels
 * concurrents avec la même marge partagent UNE promesse (déduplication de
 * l'attente, cf. §5.1). Le rafraîchissement FORCÉ (`-1`) garde sa propre entrée
 * pour qu'un rejeu de 401 ne puisse jamais hériter du « false » d'un
 * rafraîchissement opportuniste jugé inutile.
 */
const inflightRefreshes = new Map<number, Promise<boolean>>();

let refreshLoopHandle: number | null = null;

function instance(): Keycloak {
  if (!keycloak) {
    throw new Error('Keycloak n\'est pas initialisé : createKeycloak() doit être appelé au boot.');
  }
  return keycloak;
}

/** Instancie le singleton Keycloak. À appeler UNE fois, avant initKeycloak(). */
export function createKeycloak(config: RuntimeConfig): void {
  keycloak = new Keycloak({
    url: config.keycloakUrl,
    realm: config.keycloakRealm,
    clientId: config.keycloakClientId,
  });
}

/**
 * Démarre keycloak-js. AWAITÉ avant `app.mount()`.
 * Résout `true` si une session SSO existait déjà.
 *
 * `check-sso` (et pas `login-required`) : l'app doit pouvoir démarrer non
 * authentifiée, c'est le guard de route qui déclenche `login()`.
 *
 * ⚠️ BORNÉE DANS LE TEMPS. En `check-sso` + `checkLoginIframe: false` +
 * `silentCheckSsoRedirectUri`, keycloak-js 25.0.6 ouvre une iframe cachée et
 * attend son `postMessage` — SANS AUCUN timeout (`checkSsoSilently`, dist/
 * keycloak.mjs:209-231). Keycloak injoignable, iframe bloquée (CSP, extension,
 * cookies tiers) : la promesse ne se règle jamais, `await initKeycloak()` de
 * `main.ts` reste suspendu, ni `mount()` ni le `catch` du boot ne s'exécutent —
 * page blanche définitive, exactement ce que §5.4-7 interdit. On borne donc
 * l'attente pour que l'échec remonte au `catch` et monte `<BootErrorScreen>`.
 */
export function initKeycloak(): Promise<boolean> {
  let timer: number | undefined;

  const timeout = new Promise<never>((_resolve, reject) => {
    timer = window.setTimeout(
      () => reject(new Error('Service d\'authentification injoignable.')),
      INIT_TIMEOUT_MS,
    );
  });

  return Promise.race([
    instance().init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      checkLoginIframe: false,
      pkceMethod: 'S256',
    }),
    timeout,
  ]).finally(() => {
    if (timer !== undefined) window.clearTimeout(timer);
  });
}

export function isAuthenticated(): boolean {
  return keycloak?.authenticated === true;
}

export function getToken(): string | undefined {
  return keycloak?.token;
}

/**
 * Rafraîchit si le token expire dans moins de `minValidity` s (`-1` = forcé).
 *
 * CONTRAT DUR — **ne REJETTE JAMAIS**. Résout `true` si le token a été rafraîchi,
 * `false` si ce n'était pas nécessaire OU si le rafraîchissement est impossible.
 *
 * keycloak-js 25.0.6 rejette `updateToken()` quand il n'y a plus de refresh token
 * et appelle `clearToken()` sur un refresh en 400 : sans ce contrat, une rejection
 * brute remonterait à l'appelant (intercepteur de requête, boucle de fond) au lieu
 * d'un `ApiError`, et la branche « échec ⇒ login() » ne serait jamais atteinte.
 */
export function ensureFreshToken(minValidity: number = DEFAULT_MIN_VALIDITY): Promise<boolean> {
  if (!keycloak) return Promise.resolve(false);

  const inflight = inflightRefreshes.get(minValidity);
  if (inflight) return inflight;

  const kc = keycloak;
  const pending = (async (): Promise<boolean> => {
    try {
      return await kc.updateToken(minValidity);
    } catch {
      return false;
    }
  })();

  inflightRefreshes.set(minValidity, pending);
  void pending.finally(() => {
    if (inflightRefreshes.get(minValidity) === pending) {
      inflightRefreshes.delete(minValidity);
    }
  });

  return pending;
}

/** Redirige vers Keycloak. `redirectUri` par défaut = window.location.href. */
export function login(redirectUri?: string): Promise<void> {
  return instance().login({ redirectUri: redirectUri ?? window.location.href });
}

/** Déconnexion Keycloak, retour sur la racine de l'app. */
export function logout(): Promise<void> {
  return instance().logout({ redirectUri: window.location.origin });
}

/** Claims d'affichage UNIQUEMENT (nom, email de secours). Le RÔLE ne vient JAMAIS d'ici. */
export function tokenClaims(): Record<string, unknown> | undefined {
  return keycloak?.tokenParsed as Record<string, unknown> | undefined;
}

/**
 * Boucle de fond : toutes les 15 s, rafraîchit le token s'il arrive à expiration.
 * À armer APRÈS le mount. Idempotente (un seul intervalle par page).
 *
 * ⚠️ On ne renvoie PAS sur Keycloak au simple « pas rafraîchi » : `ensureFreshToken`
 * résout `false` aussi bien quand le refresh était inutile que quand il est
 * impossible (§5.1). Le signal d'échec réel est la perte de session côté
 * keycloak-js (`clearToken()` sur un refresh en 400) — c'est lui qu'on teste.
 */
export function startTokenRefreshLoop(): void {
  if (refreshLoopHandle !== null) return;

  refreshLoopHandle = window.setInterval(() => {
    if (!isAuthenticated()) return;
    void ensureFreshToken().then(() => {
      if (!isAuthenticated() || !getToken()) {
        void login();
      }
    });
  }, REFRESH_LOOP_MS);
}
